import { Link } from 'react-router-dom'
import { CurrencyDollarIcon, UserGroupIcon, CalendarIcon } from '@heroicons/react/24/outline'

export default function Home() {
  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8">
      {/* Hero Section */}
      <div className="text-center mb-16 sm:mb-24 pt-10 sm:pt-16">
        <h1 className="text-4xl sm:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
          Welcome to <span className="text-primary-600">BoxHand</span>
        </h1>
        <p className="text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
          A modern platform for rotating savings and credit associations (ROSCAs).
          <span className="block text-primary-600 font-medium mt-2">Save together, support each other, and achieve your financial goals—with fair schedules, guardrails, and easy cloning for repeat cycles.</span>
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-8 py-3.5 border border-transparent text-base font-semibold rounded-full text-white bg-primary-600 hover:bg-primary-700 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
          >
            Get Started
          </Link>
        </div>
      </div>

      {/* Features */}
      <div className="grid md:grid-cols-3 gap-8 mt-16 max-w-7xl mx-auto">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-primary-50 text-primary-600 mb-6">
            <CurrencyDollarIcon className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Flexible Contributions
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Set your own contribution amounts and cycle durations. Track all contributions and payouts manually with our easy-to-use ledger.
          </p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-primary-50 text-primary-600 mb-6">
            <UserGroupIcon className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Easy Member Management
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Invite members via email in seconds. Manage membership details and track participation status effortlessly.
          </p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-primary-50 text-primary-600 mb-6">
            <CalendarIcon className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Fair Scheduling
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Multi-payout scheduling with per-member desired payouts, shared slots, and automatic guardrails so monthly payouts never exceed contributions.
          </p>
        </div>
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow md:col-span-3 lg:col-span-1">
          <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-primary-50 text-primary-600 mb-6">
            <CalendarIcon className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Clone & Reuse
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Organizers can clone prior boxes to carry over members, schedules, and settings with a fresh start date—no rework needed.
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="mt-24 bg-white p-8 sm:p-12 rounded-3xl shadow-sm border border-gray-100 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">How It Works</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="relative">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary-600 text-white font-bold text-lg shadow-md z-10 relative">
                1
              </div>
              <div className="hidden lg:block absolute left-10 right-0 h-0.5 bg-gray-100 top-5 -z-0"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Create or Clone</h3>
            <p className="text-gray-600 text-sm">
              Set up your rotating savings box with contribution amount and cycle duration, or clone a previous box you organize.
            </p>
          </div>

          <div className="relative">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary-600 text-white font-bold text-lg shadow-md z-10 relative">
                2
              </div>
              <div className="hidden lg:block absolute left-10 right-0 h-0.5 bg-gray-100 top-5 -z-0"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Invite Members</h3>
            <p className="text-gray-600 text-sm">
              Send email invitations to friends and family to join your savings circle.
            </p>
          </div>

          <div className="relative">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary-600 text-white font-bold text-lg shadow-md z-10 relative">
                3
              </div>
              <div className="hidden lg:block absolute left-10 right-0 h-0.5 bg-gray-100 top-5 -z-0"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Start Contributing</h3>
            <p className="text-gray-600 text-sm">
              Members contribute each month, and one or more members receive payouts per slot based on the saved schedule.
            </p>
          </div>

          <div className="relative">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary-600 text-white font-bold text-lg shadow-md z-10 relative">
                4
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Track Everything</h3>
            <p className="text-gray-600 text-sm">
              Keep track of all contributions and payouts with our built-in ledger and guardrails that block overpaying.
            </p>
          </div>
        </div>
      </div>

    </div>
  )
}
